/**
 * `decal-layers.ts` one model up: separating the region's **ground plates**
 * from each other in the order the original paints them.
 *
 * ## The bug this exists for (user report, 2026-08-26)
 *
 * *"some ground triangles switch color based on angle/player location. for
 * example the ground can be brown and then suddenly switch to all green."*
 *
 * A region's landcover is a stack of flat plates, every one of them authored at
 * `z = 0` in its own frame, and in five of the six shipped regions several of
 * those plates **cover the same ground**. `decal-layers.ts` is what normally
 * arbitrates a coplanar overlap — but it runs over *one model's* triangles, and
 * these plates are different models (`TERR5-10` and `TERR5-72` in Verdun,
 * `TERR1-01` and `TERR1-71` in London), so it never sees the pair.
 * `world-shared-ref.ts` already records this for the sea: *"the 4 mm nudge is
 * per model, so it does nothing across two of them. The two z-fight."* The sea
 * was given a metre of draught; nothing was done for the land.
 *
 * Two independent measurements of how much ground that is. **Pairs** of plates
 * that share ground at heights a depth buffer cannot tell apart, which is what
 * `coplanarPlatePairs` counts and `test/plate-paint-order.test.ts` pins; and
 * the **area** those pairs disagree about, sampled on a 50 m grid over every
 * placed face at the exact datum, counting only cells where the two plates
 * paint *different* colours:
 *
 * | region  | pairs | ground covered | contested area | nearest to spawn |
 * |---------|-------|----------------|----------------|------------------|
 * | Verdun  |  24   |     2 720 km²  |      60.4 km²  |  0.1 km |
 * | Somme   |  37   |     5 149 km²  |      98.5 km²  |  0.3 km |
 * | Paris   |  30   |     2 500 km²  |      16.3 km²  | 14.1 km |
 * | Dunkirk |  21   |     3 361 km²  |      14.6 km²  |  0.0 km |
 * | London  |   7   |     3 806 km²  |      32.5 km²  |  4.7 km |
 *
 * ## Why it looks like the camera is choosing the colour
 *
 * Because it is. Two plates authored at the same datum and draped through the
 * same sampler onto the same lattice land on the same depth value, and
 * `THREE.Material`'s default `depthFunc` is `LessEqualDepth` — reversed to
 * `GreaterEqual` by `flight-view.ts`'s reversed-Z buffer — so **a tie is won by
 * whichever plate draws last**. Draw order is not the region's: three's
 * `RenderList` sorts opaque meshes by `geometry.boundingSphere.center`
 * projected through the view-projection matrix
 * (`renderers/common/Renderer.js`), and the WebGPU `painterSortStable` has no
 * material tiebreak before that key. A 71 km plate's bounding-sphere centre is
 * nowhere near the ground the pilot is looking at, so the two keys cross at
 * ordinary poses — the test drives twelve bearings around one contested Verdun
 * square at 2 000 ft and 3 km out and gets **two different colours** out of it.
 *
 * ## The fix, and why it is a fidelity gain rather than a patch
 *
 * The original had no depth buffer: it painted these plates back to front in
 * `.WLD` record order, and the later record won. `world-instancer.ts` already
 * keeps that order deliberately — it is what `polygon-outlines.ts` resolves
 * coincident terrain edges by — so the surfaces were being decided by the
 * camera while the *hairlines drawn on them* were being decided by paint order,
 * and the two could disagree. Sinking each covered plate clear of the ones
 * painted over it makes the depth buffer agree with the painter, and with the
 * road network — and leaves the plate the pilot actually sees exactly where it
 * has always been.
 *
 * A **whole-plate** move rather than a per-triangle one, for one reason that
 * matters: a plate's road and river outlines are read off its geometry in
 * `world-shared-ref.ts` before this runs (`triangleOutlineSegments`), so the
 * move has to be something the segments can be given too — otherwise a sunken
 * field would leave its own roads hanging above it. A uniform offset per plate
 * is; a per-triangle one is not without re-deriving the outlines.
 *
 * Measured over all five regions: the bottom of the deepest stack drops
 * **28-40 mm**, and the pass costs ~10 ms once at region load — against a
 * region build that already spends 20-35 ms sampling the height field.
 * `test/plate-paint-order.test.ts` pins the sizes and the before/after.
 */

import type * as THREE from "three/webgpu";
import {
  project,
  trianglesOverlap,
  DECAL_LAYER_OFFSET_M,
  NORMAL_DOT_MIN,
  PLANE_DISTANCE_TOLERANCE_M,
  type Triangle2,
} from "./decal-layers.ts";
import { wantsVertexDrape } from "./terrain-drape.ts";
import { placementWorldTransform, type PlacementOrigin } from "../world/placement-transform.ts";
import type { InstancerPlacement, SharedRef } from "./world-shared-ref.ts";

/** The clearance a plate keeps over the one painted under it — `decal-layers.ts`'s own 4 mm, for the same reason and at the same scale. */
export const PLATE_LAYER_LIFT_M = DECAL_LAYER_OFFSET_M;

/**
 * The clearance the **hairline network** keeps over the ground it is drawn on
 * (user report B3, 2026-08-25: *"roads are z-fighting on the ground"*).
 *
 * ## Why there was nothing to arbitrate at all
 *
 * A road, a river and a field boundary are all one thing in this port: a
 * screen-space-wide line in the merged buffer `polygon-outlines.ts` builds
 * (`hairline-mesh.ts` draws it). Its endpoints are not *near* the plate under
 * them — `world-shared-ref.ts` reads them **off that plate's own position
 * buffer** with `triangleOutlineSegments`, and `terrain-drape.ts` then drapes
 * both through the same sampler on the same lattice cuts. Measured through the
 * production loaders over the real bake, the gap between a hairline and the
 * plate face beneath it has median **0 m** and worst case 8e-8 m across all
 * five regions: they are the same float. `separatePlatesByPaintOrder` above
 * settles plate against plate and `terrain-drape.ts`'s 8 cm settles everything
 * against the relief mesh, but nothing had ever separated a line from the
 * surface it was traced from.
 *
 * So the winner was three's opaque sort — the hairline mesh spans the whole
 * region, its bounding-sphere sort key is nowhere near the ground in frame, and
 * it crosses a plate's key as the pilot flies. Reversing that one ordering at a
 * held pose (the `lessons.md` 2026-08-23 instrument, with both orderings
 * captured twice so the propeller's own motion is excluded) repaints **32-70 px
 * of every frame** at 430-1,610 ft over Verdun, in flat palette pairs — road
 * grey `#797979` against field green `#4d5928` — and it does it with ENHANCED
 * TERRAIN both on and off.
 *
 * ## Why this number and not another
 *
 * **Half `PLATE_LAYER_LIFT_M`, and expressed as half of it rather than as its
 * own 2 mm**, because the two are one rule and a lane that tunes the plate step
 * must not silently leave this behind. The plates above are stacked a whole
 * `PLATE_LAYER_LIFT_M` apart, so a lift of strictly less than that step cannot
 * carry a *hidden* plate's roads up through the plate painted over it — a
 * sunken field that let its own roads through would be this bug wearing the
 * previous one's clothes. Half the step is the largest value with that property
 * that is not a boundary case.
 *
 * And it is enough at every range the world is drawn at. `flight-view.ts` runs
 * a reversed-Z **float32** buffer over 1 m - 26 km; for `d = near/z` a one-ulp
 * step in `d` is `z * 2^-24` of world depth, i.e. the buffer resolves **0.06 mm
 * at 1 km and 1.55 mm at the far plane**. 2 mm clears that everywhere in the
 * drawn world, and it is 25 000 times below the 50 m a road subtends at the
 * range it stops being one pixel wide.
 */
export const HAIRLINE_LIFT_M = PLATE_LAYER_LIFT_M / 2;

/**
 * Raises every hairline endpoint clear of the surface it was traced off.
 *
 * Applied to the merged, already-draped world buffer rather than per plate,
 * because by then a segment has no owner: `buildSegmentBuffers` has folded
 * every ref's outlines into one buffer and dropped the duplicates. A uniform
 * lift is exactly what that allows — it preserves every ordering the paint-order
 * pass established, since it moves all of them by the same amount.
 */
export function liftHairlinesOffGround(positions: Float32Array): void {
  for (let at = 1; at < positions.length; at += 3) positions[at] = positions[at]! + HAIRLINE_LIFT_M;
}

/** Straight up, because a ground plate's own winding is not a promise about which way is up — `terrain-drape.ts`'s `shadedPlateColours` settles the same question the same way. */
const UP: readonly [number, number, number] = [0, 1, 0];

/**
 * One candidate plate, in `.WLD` record order. `geometry` is the shared,
 * already-oriented geometry `world-shared-ref.ts` built; `segments` is the
 * outline buffer read off it, which has to move with it.
 */
export interface PlateCandidate {
  readonly geometry: THREE.BufferGeometry;
  /** Mutated in place: six floats per segment, world space. */
  readonly segments: { readonly positions: Float32Array };
  readonly boundingRadius: number;
  readonly placementCount: number;
  /** The single placement's own translation — these are world geometry already, and `terrain-drape.ts` records that every shipped one is placed at rotation 0. */
  readonly originX: number;
  readonly originZ: number;
}

/**
 * One loaded ref as a candidate. A ref that failed to load keeps its slot as
 * `undefined` rather than being filtered out, because the order the pass runs
 * in has to stay the region's `.WLD` record order — that *is* the paint order
 * everything below arbitrates by.
 */
export function plateCandidateFor(
  shared: SharedRef | undefined,
  refPlacements: readonly InstancerPlacement[],
  origin: PlacementOrigin,
): PlateCandidate | undefined {
  if (!shared) return undefined;
  const only = placementWorldTransform(refPlacements[0]!, origin);
  return {
    geometry: shared.geometry,
    segments: shared.segments,
    boundingRadius: shared.boundingRadius,
    placementCount: refPlacements.length,
    originX: only.position.x,
    originZ: only.position.z,
  };
}

/** A plate reduced to what the overlap test needs: its near-horizontal faces, projected, with their heights. */
interface Footprint {
  readonly faces: readonly { readonly flat: Triangle2; readonly y: number }[];
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  /** The plate's own vertical span — `decal-layers.ts` may already have stacked its faces millimetres apart inside the model. */
  readonly minY: number;
  readonly maxY: number;
}

/**
 * The near-horizontal faces of one plate, in world XZ.
 *
 * Only the horizontal ones, because "which plate is on top" is a vertical
 * question and a plate's vertical faces (a river bank, a cutting) are not what
 * ties. `NORMAL_DOT_MIN` is the same ~2.5° cone `decal-layers.ts` calls one
 * plane.
 */
function footprintOf(candidate: PlateCandidate): Footprint | undefined {
  const position = candidate.geometry.getAttribute("position");
  if (!position) return undefined;
  const p = position.array as ArrayLike<number>;
  const faces: { flat: Triangle2; y: number }[] = [];
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  for (let t = 0; t + 8 < p.length; t += 9) {
    const ax = p[t]! + candidate.originX, ay = p[t + 1]!, az = p[t + 2]! + candidate.originZ;
    const bx = p[t + 3]! + candidate.originX, by = p[t + 4]!, bz = p[t + 5]! + candidate.originZ;
    const cx = p[t + 6]! + candidate.originX, cy = p[t + 7]!, cz = p[t + 8]! + candidate.originZ;
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const length = Math.hypot(nx, ny, nz);
    if (length === 0 || Math.abs(ny) / length < NORMAL_DOT_MIN) continue;
    const flat: Triangle2 = [project([ax, ay, az], UP), project([bx, by, bz], UP), project([cx, cy, cz], UP)];
    faces.push({ flat, y: (ay + by + cy) / 3 });
    minY = Math.min(minY, ay, by, cy);
    maxY = Math.max(maxY, ay, by, cy);
    for (const corner of flat) {
      minX = Math.min(minX, corner[0]);
      maxX = Math.max(maxX, corner[0]);
      minZ = Math.min(minZ, corner[1]);
      maxZ = Math.max(maxZ, corner[1]);
    }
  }
  return faces.length > 0 ? { faces, minX, maxX, minZ, maxZ, minY, maxY } : undefined;
}

/** True when the two plates share ground within `toleranceM` of the same height. */
function platesCollide(a: Footprint, b: Footprint, toleranceM: number): boolean {
  if (a.minX > b.maxX || b.minX > a.maxX || a.minZ > b.maxZ || b.minZ > a.maxZ) return false;
  for (const faceA of a.faces) {
    for (const faceB of b.faces) {
      if (Math.abs(faceA.y - faceB.y) > toleranceM) continue;
      if (trianglesOverlap(faceA.flat, faceB.flat)) return true;
    }
  }
  return false;
}

/**
 * The height difference below which two ground surfaces are, to this app's
 * depth buffer, the same surface.
 *
 * A millimetre, and it is not a guess: `flight-view.ts` runs a reversed-Z
 * **float32** depth buffer over a 1 m / 26 km range, whose resolution is
 * `z · 2^-24`-ish and reaches ~0.4 mm at the far plane, so anything closer than
 * a millimetre cannot be arbitrated anywhere in the drawn world.
 * `decal-layers.ts` picked its 4 mm against the same argument.
 */
export const DEPTH_SEPARATION_FLOOR_M = 0.001;

/**
 * How many *pairs* of candidate plates share ground within `toleranceM` — i.e.
 * how many colour ties are left for the camera to break. The measurement the
 * 2026-08-26 report reduces to, and the one `liftPlatesByPaintOrder` has to
 * drive to zero.
 */
export function coplanarPlatePairs(
  candidates: readonly (PlateCandidate | undefined)[],
  toleranceM: number = DEPTH_SEPARATION_FLOOR_M,
): number {
  const footprints = candidateFootprints(candidates);
  let pairs = 0;
  for (let i = 0; i < footprints.length; i++) {
    const above = footprints[i];
    if (!above) continue;
    for (let j = 0; j < i; j++) {
      const below = footprints[j];
      if (below && platesCollide(above, below, toleranceM)) pairs++;
    }
  }
  return pairs;
}

function candidateFootprints(candidates: readonly (PlateCandidate | undefined)[]): (Footprint | undefined)[] {
  return candidates.map((candidate) =>
    candidate && wantsVertexDrape(candidate.boundingRadius, candidate.placementCount)
      ? footprintOf(candidate)
      : undefined,
  );
}

/**
 * How far, in metres, every candidate has to be **sunk** to get out of the way
 * of every plate painted after it that covers it: `0` for a plate nothing is
 * painted over, and otherwise enough that the plate above's **lowest** face
 * clears this plate's **highest** by `PLATE_LAYER_LIFT_M`.
 *
 * **Down, not up, and that is the whole reason this returns drops.** The plate
 * left at zero is the last one painted — the one the original draws on top and
 * therefore the one the pilot sees and lands on. Lifting the winner instead
 * would move the drawn ground off the height `flight/ground-contact.ts` puts
 * the wheels at, which is the shape of the 2026-08-24 fall-through report; only
 * the plates that are *hidden underneath* move here, and where they are is by
 * definition not visible.
 *
 * **A fixed step per layer was the first version and it was wrong**, caught by
 * the measurement rather than by reading: `decal-layers.ts` has already stacked
 * some of these plates' own faces 4, 8 and 12 mm apart inside one model, so a
 * 4 mm-per-layer step walks a plate straight onto its neighbour's second decal
 * layer and re-creates the tie one storey down. Clearing the whole plate is the
 * rule that cannot do that, and it stays cheap: 28-40 mm at the bottom of the
 * deepest stack in any shipped region, against the 80 mm of datum clearance the
 * plates have over the relief mesh, so nothing sinks through the ground.
 *
 * Monotone in record order by construction — if `j < i` and the two collide,
 * every face of `i` ends up above every face of `j` — which is what makes the
 * depth buffer reproduce the original's back-to-front paint instead of a coin
 * flip. Non-candidates (anything but a once-placed region-scale plate) get `0`
 * and are never moved: a geometry many placements share cannot be moved for one
 * of them, and the fights that matter are all between the big single-placement
 * plates.
 */
export function platePaintOrderDrops(candidates: readonly (PlateCandidate | undefined)[]): Float64Array {
  const drops = new Float64Array(candidates.length);
  const footprints = candidateFootprints(candidates);
  // Descending, so a plate's own drop is final before anything it covers reads
  // it — the dependency runs from later records to earlier ones.
  for (let above = footprints.length - 1; above > 0; above--) {
    const upper = footprints[above];
    if (!upper) continue;
    for (let below = 0; below < above; below++) {
      const lower = footprints[below];
      if (!lower) continue;
      // `PLANE_DISTANCE_TOLERANCE_M` here rather than the depth floor: the
      // question at layering time is "did the artist mean these to be one
      // surface", which is `decal-layers.ts`'s question and takes its answer.
      if (!platesCollide(upper, lower, PLANE_DISTANCE_TOLERANCE_M)) continue;
      const needed = drops[above]! + lower.maxY - upper.minY + PLATE_LAYER_LIFT_M;
      if (needed > drops[below]!) drops[below] = needed;
    }
  }
  return drops;
}

/**
 * Sinks every covered plate out of the way of the ones painted over it,
 * geometry and outline segments together, and returns the largest drop applied
 * (metres) so a caller can report it rather than assume it.
 */
export function separatePlatesByPaintOrder(candidates: readonly (PlateCandidate | undefined)[]): number {
  const drops = platePaintOrderDrops(candidates);
  let largest = 0;
  candidates.forEach((candidate, index) => {
    const drop = drops[index]!;
    if (!candidate || drop === 0) return;
    largest = Math.max(largest, drop);
    candidate.geometry.translate(0, -drop, 0);
    // The outlines were read off this geometry before it moved, and a road left
    // behind is a road the field above now covers.
    const positions = candidate.segments.positions;
    for (let at = 1; at < positions.length; at += 3) positions[at] = positions[at]! - drop;
  });
  return largest;
}
